import * as React from 'react';
import { produce } from 'immer';
import { FormattedHTMLMessage } from 'react-intl';
import { VerificationService,
         SSOViewModel,
         SSOResponse,
         StudentPersonalInfoViewModel,
         logger,
         VerificationStepsEnum,
         setTabRef,
         getTabRef,
         closeTabRef,
} from '@sheerid/jslib';

const defaultSubtitle = "Please sign in to your school's web portal. It will open in a new tab.";

const defaultSubtitle2 = "Can't sign in to your school?";

interface StepSSOProps {
    verificationService: VerificationService;
}

const StepSSOComponent = ({ verificationService }: StepSSOProps) => {
    const programId = verificationService.programId;
    const viewModel = verificationService.viewModel as SSOViewModel;
    const previousViewModel = (verificationService.previousViewModel as StudentPersonalInfoViewModel) || undefined;
    const verificationResponse = verificationService.verificationResponse as SSOResponse;
    const previousVerificationResponse = verificationService.previousVerificationResponse || undefined;
    const verificationId = verificationService.verificationResponse.verificationId;
    let timeout: any;

    const pollForVerificationServiceUpdates = () => setTimeout(
        () => {
            verificationService.fetchExistingVerificationRequest(programId, verificationId, previousVerificationResponse);
        },
        1000,
    );

    const updateViewModel = (key: any, value: any) => {
        const nextState = produce(viewModel, (draft) => {
            draft[key] = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const cancelSSOStep = () => {
        closeTabRef();
        const nextState = produce(viewModel, () => {});
        logger.info('StepSSOComponent submitting cancel request');
        clearTimeout(timeout);
        verificationService.submitStep(VerificationStepsEnum.sso, nextState, verificationService.verificationResponse);
    };

    const updateViewModelAndOpenLoginTab = (key: any, value: any) => {
        updateViewModel(key, value);
        openLoginTab();
    };

    const openLoginTab = () => {
        setTabRef(window.open(verificationResponse.loginUrl, '_blank'));
        getTabRef().focus();
    };

    if (viewModel.isSSOActive) {
        timeout = pollForVerificationServiceUpdates();
    }

    if (!viewModel.countryChoice && previousViewModel && previousViewModel.countryChoice) {
        updateViewModel('countryChoice', previousViewModel.countryChoice);
    }

    return (
        <div id="sid-step-sso" className="sid-l-container">
            {
                viewModel.isSSOActive ?
                    <div className="sid-header sid-l-horz-center">
                        <div className="sid-l-horz-center">
                        </div>
                        <div className="sid-verification_title sid-l-horz-center sid-l-space-top-md">
                            <FormattedHTMLMessage id="step.docUpload.verificationTitle" defaultMessage="STUDENT VERIFICATION" />
                        </div>
                        <div className="sid-header__title sid-l-horz-center sid-l-space-top-md">
                            <FormattedHTMLMessage id="step.sso.title" defaultMessage="Verify using your school credentials" />
                        </div>
                        <div className="sid-header__subtitle-one">
                            <FormattedHTMLMessage
                                id="step.sso.subtitle"
                                defaultMessage={defaultSubtitle}
                            />
                        </div>
                        <div className="sid-l-space-top-xl" />
                        <div className="sid-header__subtitle-two sid-h-small-text sid-l-space-top-md">
                            <FormattedHTMLMessage
                                id="step.sso.subtitle2"
                                defaultMessage={defaultSubtitle2}
                            />
                            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                                <button
                                    onClick={cancelSSOStep}
                                    type="submit"
                                    className="sid-btn sid-btn--dark sid-l-full-width"
                                    aria-label="submit"
                                >
                                    <FormattedHTMLMessage id="step.sso.cancel" defaultMessage="Upload Proof of Enrollment" />
                                </button>
                            </div>
                        </div>
                        <div className="sid-l-space-top-xl" />
                    </div>
                :
                    <div className="sid-header sid-l-horz-center">
                        <div className="sid-l-horz-center">
                        </div>
                        <div className="sid-verification_title sid-l-horz-center sid-l-space-top-md">
                            <FormattedHTMLMessage id="step.docUpload.verificationTitle" defaultMessage="STUDENT VERIFICATION" />
                        </div>
                        <div className="sid-header__title sid-l-horz-center sid-l-space-top-md">
                            <FormattedHTMLMessage id="step.sso.title" defaultMessage="Verify using your school credentials" />
                        </div>
                        <div className="sid-header__subtitle-one">
                            <FormattedHTMLMessage
                                id="step.sso.subtitle"
                                defaultMessage={defaultSubtitle}
                            />
                        </div>
                        <div className="sid-l-space-top-xl" />
                        <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                            <button
                                id="sid-submit-btn-sso"
                                onClick={() => updateViewModelAndOpenLoginTab('isSSOActive', true)}
                                type="submit"
                                className="sid-btn sid-btn--dark sid-l-full-width"
                                aria-label="submit"
                            >
                                <FormattedHTMLMessage id="step.sso.login" defaultMessage="Sign in to my school" />
                            </button>
                        </div>
                        <div className="sid-sso-details">
                            <FormattedHTMLMessage id="step.sso.details" defaultMessage="Information entered into this web form will be used for verification purposes. The information will be shared with The Wall Street Journal. Your information will be transferred to the United States and will be treated according to SheerID’s privacy policy." />
                        </div>
                        <div className="sid-l-space-top-xl" />
                    </div>
            }
        </div>
    );
};

export { StepSSOComponent };
